///<reference lib="dom"/>
import {
  SwCachePrefix,
  type SwCacheSummary,
  SwDeleteCaches,
  type SwInfo,
  SwListCaches,
  type SwReply,
  type SwRequest,
} from "../protocol.tsx";

export function SwScope(swUrl = "sw.js") {
  return new URL("./", new URL(swUrl, document.baseURI)).href;
}

export interface SwRegisterProps {
  enabled: boolean;
  url?: string;
}
export async function SwRegister(
  props: SwRegisterProps,
): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  const url = new URL(props.url ?? "sw.js", document.baseURI).href;
  const scope = SwScope(url);

  if (!props.enabled) {
    const reg = await navigator.serviceWorker.getRegistration(scope);
    if (reg && reg.scope === scope) await reg.unregister();
    return null;
  }

  return await navigator.serviceWorker.register(url, { scope, updateViaCache: "none" });
}

export interface SwWatchProps {
  reg: ServiceWorkerRegistration;
  onWaiting: (worker: ServiceWorker) => void;
}
export function SwWatch(props: SwWatchProps) {
  const { reg } = props;
  const hadController = !!navigator.serviceWorker.controller;
  let reloading = false;

  const check = () => {
    if (reg.waiting && navigator.serviceWorker.controller) props.onWaiting(reg.waiting);
  };
  const onFound = () => {
    const worker = reg.installing;
    worker?.addEventListener("statechange", () => {
      if (worker.state === "installed") check();
    });
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") reg.update().catch(() => {});
  };
  const onController = () => {
    if (!hadController || reloading) return;
    reloading = true;
    location.reload();
  };

  check();
  reg.addEventListener("updatefound", onFound);
  document.addEventListener("visibilitychange", onVisible);
  navigator.serviceWorker.addEventListener("controllerchange", onController);

  return () => {
    reg.removeEventListener("updatefound", onFound);
    document.removeEventListener("visibilitychange", onVisible);
    navigator.serviceWorker.removeEventListener("controllerchange", onController);
  };
}

export function SwApplyUpdate(reg: ServiceWorkerRegistration) {
  const msg: SwRequest = { type: "sw/skipWaiting" };
  reg.waiting?.postMessage(msg);
}

export function SwAsk(worker: ServiceWorker, msg: SwRequest, timeoutMs = 3000): Promise<SwReply> {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => reject(new Error(`no reply to ${msg.type}`)), timeoutMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      resolve(event.data as SwReply);
    };
    worker.postMessage(msg, [channel.port2]);
  });
}

export async function SwInfoQuery(reg: ServiceWorkerRegistration): Promise<SwInfo | null> {
  if (!reg.active) return null;
  const reply = await SwAsk(reg.active, { type: "sw/info" });
  return reply.type === "sw/info" ? reply.info : null;
}

export interface SwVersion {
  release: string;
  digest: string;
}
export async function SwFetchVersion(running: string): Promise<SwVersion | null> {
  try {
    const url = new URL(`version.json?r=${encodeURIComponent(running)}`, document.baseURI);
    const res = await fetch(url, { cache: "no-store" });
    return res.ok ? await res.json() as SwVersion : null;
  } catch {
    return null;
  }
}

export function SwScopeCaches(app: string): Promise<SwCacheSummary[]> {
  return SwListCaches(SwCachePrefix(app, SwScope()));
}

export async function SwStorage() {
  const estimate = await navigator.storage?.estimate?.();
  const persisted = await navigator.storage?.persisted?.();
  return { usage: estimate?.usage ?? 0, quota: estimate?.quota ?? 0, persisted: !!persisted };
}

export async function SwPersist() {
  return await navigator.storage?.persist?.() ?? false;
}

export interface SwResetProps {
  app: string;
  reload?: boolean;
}
export async function SwReset(props: SwResetProps) {
  const scope = SwScope();
  const deleted = await SwDeleteCaches(SwCachePrefix(props.app, scope));
  let unregistered = false;
  if ("serviceWorker" in navigator) {
    for (const reg of await navigator.serviceWorker.getRegistrations()) {
      if (reg.scope === scope) unregistered = (await reg.unregister()) || unregistered;
    }
  }
  if (props.reload !== false) location.replace(scope);
  return { deleted, unregistered };
}

export interface SwResetMainProps {
  app: string;
}
export function SwResetMain(props: SwResetMainProps) {
  const status = document.querySelector<HTMLElement>("[data-sw-status]");
  const button = document.querySelector<HTMLButtonElement>("[data-sw-reset]");
  const say = (text: string) => {
    if (status) status.textContent = text;
  };

  SwScopeCaches(props.app)
    .then((list) => say(`${list.length} cache(s) for this app`))
    .catch(() => say("caches unavailable"));

  button?.addEventListener("click", async () => {
    button.disabled = true;
    say("resetting…");
    await SwReset({ app: props.app });
  });
}
